import { SetContextLink } from '@apollo/client/link/context'
import { Errors, UserFieldsFragment } from '@/graphql/generated/graphql'
import { toast } from '@/hooks/useToast'
import {
  getAccessTokenRefreshedIfPossible,
  setTokens,
  setUser,
  logout,
  getMaintainerAdminSelectedTeam,
} from '@/hooks/useAuth/zustand'
import { ApolloLink, CombinedGraphQLErrors } from '@apollo/client'
import { getSelectedEducationLevelType } from '../EducationLevelsContext/educationLevelState'
import { ErrorLink } from '@apollo/client/link/error'
import { map } from 'rxjs/operators' // Or import { map } from 'rxjs/operators' for older RxJS
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { usePregeneratedHashes } from 'graphql-codegen-persisted-query-ids/lib/apollo'
import hashes from '@/graphql/generated/persistedQueriesMapClient.json'

export const authRequestLink = new SetContextLink(
  async (prevContext, operation) => {
    if (typeof window === 'undefined') return prevContext

    const token = await getAccessTokenRefreshedIfPossible(
      operation.operationName,
    )
    const authorization = token && `bearer ${token}`
    const hostpath =
      typeof window !== 'undefined' ? window.location.href : 'ServerSide'
    const teamId = getMaintainerAdminSelectedTeam()
    const selectedLevel = getSelectedEducationLevelType()

    return {
      headers: {
        ...prevContext.headers, // keep haeders directly set in query/mutation
        ...(authorization ? { authorization } : {}),
        ...(teamId && authorization ? { teamId } : {}),
        ...(selectedLevel && authorization ? { selectedLevel } : {}),
        hostpath,
        'Apollo-Require-Preflight': 'true',
      },
    }
  },
)

export const errorLink = new ErrorLink(
  ({ error, result }: ErrorLink.ErrorHandlerOptions) => {
    const translateEachMessage = (message: string): string => {
      if (message === 'Unauthorized') return 'Não autorizado'
      if (message === 'Not authorized') return 'Não autorizado'
      return message
    }

    // Verifica se é um erro GraphQL
    if (CombinedGraphQLErrors.is(error)) {
      const graphQLErrors = error.errors

      const errorMessages = graphQLErrors.map(
        ({ message, locations, path, extensions }) => {
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
              locations,
            )}, Path: ${path}`,
          )
          console.log({ locations, extensions })
          const exception = extensions?.exception as {
            response: {
              message: string[] | string
            }
          }
          const responseMessage = exception?.response?.message
          const multipleErrors = typeof responseMessage !== 'string'
          const firstError = multipleErrors
            ? responseMessage?.[0]
            : responseMessage
          return translateEachMessage(firstError || message)
        },
      )
      const errorMessagesUnique = errorMessages.filter(
        (value: string, index: number, array: string[]) => {
          return array.indexOf(value) === index
        },
      )

      const showToastError = (() => {
        const handableErrors = graphQLErrors.filter((el) => {
          const err = el.extensions?.code as Errors | undefined
          if (!err) return false
          return Object.values(Errors).includes(err)
        })
        const hasForceLogoutError = handableErrors.find(
          (el) => el.extensions?.code === Errors.UserNotAuthorizedForceLogout,
        )
        if (hasForceLogoutError) {
          logout()
          return true
        }

        const allErrorsIsHandable =
          handableErrors.length === graphQLErrors.length

        console.log({ handableErrors, allErrorsIsHandable })

        return !allErrorsIsHandable
      })()

      if (showToastError) {
        toast({
          title: errorMessagesUnique.join(' '),
          variant: 'destructive',
        })
      }
    } else {
      // Erro de rede ou outro tipo de erro
      console.error(`[Network error]:`, error)
    }

    if (result) {
      // this prevents the function from being canceled in the middle
      // for example: set a loading variable to off
      // ! IF ENABLED: CANT SEE ERRORS AT THE HOOK
      // result.errors = undefined
    }
  },
)

export const handleResponseLink = new ApolloLink((operation, forward) => {
  return forward(operation).pipe(
    map((response) => {
      const context = operation.getContext()
      const headers = context.response?.headers

      if (headers) {
        const access = headers.get('access')
        const refresh = headers.get('refresh')

        if (access && refresh) {
          setTokens({ access, refresh })
          const user: UserFieldsFragment = Object.entries(
            response.data ?? {},
          ).find(
            ([, value]) => value.__typename === 'CommonUser',
          )?.[1] as UserFieldsFragment

          if (user && user.id && user.email) setUser({ user })
        }
      }

      return response
    }),
  )
})

export const persistedLink = createPersistedQueryLink({
  useGETForHashedQueries: false, // Optional but allows better caching

  // This is not a hook, it's a function that generates the hash
  // eslint-disable-next-line react-hooks/rules-of-hooks
  generateHash: usePregeneratedHashes(hashes),
})
